import { ArrowLeft } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { Button } from "../ui/button";

/**
 * PageShell - Consistent layout wrapper for all pages
 * 
 * Usage:
 *   <PageShell
 *     title="Page Title"
 *     icon={SomeIcon}
 *     breadcrumbs={[{label: 'Parent'}, {label: 'Current', path: '/current'}]}
 *     actions={<Button>Action</Button>}
 *     backPath="/back"
 *     loading={isLoading}
 *     error={errorMessage}
 *   >
 *     <PageShell.Content>
 *       {page content}
 *     </PageShell.Content>
 *   </PageShell>
 */
export const PageShell = ({ 
  title,
  icon: Icon,
  breadcrumbs,
  actions,
  loading,
  error,
  backPath,
  children 
}) => {
  const navigate = useNavigate();
  
  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div className="flex items-center gap-3">
          {backPath && (
            <Button 
              variant="ghost" 
              size="icon" 
              onClick={() => navigate(backPath)}
              className="h-8 w-8"
            >
              <ArrowLeft className="h-5 w-5" />
            </Button>
          )}
          {Icon && <Icon className="h-8 w-8 text-[var(--brand-accent)]" />}
          <div>
            {breadcrumbs && breadcrumbs.length > 0 && (
              <nav className="text-sm text-[var(--text-secondary)] mb-1">
                {breadcrumbs.map((crumb, i) => (
                  <span key={i}>
                    {i > 0 && <span className="mx-1.5">/</span>}
                    {crumb.path ? (
                      <button 
                        onClick={() => navigate(crumb.path)} 
                        className="hover:underline hover:text-[var(--text-primary)]"
                      >
                        {crumb.label}
                      </button>
                    ) : (
                      <span>{crumb.label}</span>
                    )}
                  </span>
                ))}
              </nav>
            )}
            <h1 className="text-2xl sm:text-3xl font-bold text-[var(--text-primary)]">
              {title}
            </h1>
          </div>
        </div>
        {actions && <div className="flex gap-2 flex-wrap">{actions}</div>}
      </div>
      
      {/* Loading State */}
      {loading && <PageShell.Loading />}
      
      {/* Error State */}
      {error && <PageShell.Error message={error} />}
      
      {/* Content */}
      {!loading && !error && children}
    </div>
  );
};

PageShell.Loading = () => (
  <div className="flex justify-center items-center h-64">
    <div className="animate-spin h-8 w-8 border-4 border-[var(--brand-accent)] border-t-transparent rounded-full" />
  </div>
);

PageShell.Error = ({ message, onRetry }) => (
  <div className="bg-red-50 dark:bg-red-950/20 border border-red-200 dark:border-red-800 rounded-lg p-4">
    <div className="flex items-center gap-2 text-red-800 dark:text-red-200">
      <span className="font-medium">Error:</span>
      <span>{message}</span>
    </div>
    {onRetry && (
      <Button variant="outline" size="sm" onClick={onRetry} className="mt-2">
        Retry
      </Button>
    )}
  </div>
);

PageShell.Content = ({ children, className = "" }) => (
  <div className={className}>{children}</div>
);

export default PageShell;
